import { useState, useEffect } from 'react';

export const BreathingCircle = ({ isPlaying, size = 'large', currentText = null }) => {
  const [phase, setPhase] = useState('inhale');
  const [count, setCount] = useState(4);
  const [showStartText, setShowStartText] = useState(true);
  const [displayText, setDisplayText] = useState(null);

  const sizes = {
    small: 'w-32 h-32',
    medium: 'w-48 h-48',
    large: 'w-64 h-64 md:w-80 md:h-80',
  };

  // Handle fade in/out of start text
  useEffect(() => {
    if (isPlaying) {
      // Fade out start text when playing
      setShowStartText(false);
    } else {
      // Fade in start text when stopped (with slight delay for smooth transition)
      const timer = setTimeout(() => setShowStartText(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isPlaying]);

  // Update display text with animation
  useEffect(() => {
    if (currentText) {
      setDisplayText(currentText);
    }
  }, [currentText]);

  useEffect(() => {
    if (!isPlaying) {
      setPhase('inhale');
      setCount(4);
      return;
    }

    const phases = [
      { name: 'inhale', duration: 4 },
      { name: 'hold', duration: 4 },
      { name: 'exhale', duration: 4 },
      { name: 'rest', duration: 2 },
    ];

    let currentPhaseIndex = 0;
    let currentCount = phases[0].duration;

    const interval = setInterval(() => {
      currentCount -= 1;
      setCount(currentCount);

      if (currentCount <= 0) {
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        currentCount = phases[currentPhaseIndex].duration;
        setPhase(phases[currentPhaseIndex].name);
        setCount(currentCount);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  const getPhaseStyles = () => {
    switch (phase) {
      case 'inhale':
        return {
          scale: 'scale-110',
          ring: 'ring-violet-500/40',
          innerGlow: 'shadow-[0_0_80px_30px_rgba(139,92,246,0.3)]',
        };
      case 'hold':
        return {
          scale: 'scale-110',
          ring: 'ring-teal-500/40',
          innerGlow: 'shadow-[0_0_80px_30px_rgba(20,184,166,0.3)]',
        };
      case 'exhale':
        return {
          scale: 'scale-100',
          ring: 'ring-amber-500/40',
          innerGlow: 'shadow-[0_0_60px_20px_rgba(245,158,11,0.2)]',
        };
      case 'rest':
        return {
          scale: 'scale-95',
          ring: 'ring-zinc-500/20',
          innerGlow: 'shadow-[0_0_40px_10px_rgba(113,113,122,0.1)]',
        };
      default:
        return {
          scale: 'scale-100',
          ring: 'ring-violet-500/40',
          innerGlow: 'shadow-[0_0_60px_20px_rgba(139,92,246,0.2)]',
        };
    }
  };

  const styles = getPhaseStyles();

  const phaseLabels = {
    inhale: 'Breathe In',
    hold: 'Hold',
    exhale: 'Breathe Out',
    rest: 'Rest',
  };

  return (
    <div className="flex flex-col items-center gap-8" data-testid="breathing-circle">
      {/* Main circle */}
      <div className={`relative ${sizes[size]}`}>
        {/* Outer glow ring */}
        <div 
          className={`
            absolute inset-0 rounded-full
            ring-2 ${styles.ring}
            transition-all duration-1000 ease-in-out
            ${styles.scale}
          `}
        />
        
        {/* Middle ring */}
        <div 
          className={`
            absolute inset-4 rounded-full
            bg-gradient-to-br from-zinc-800/50 to-zinc-900/50
            backdrop-blur-sm border border-white/5
            transition-all duration-1000 ease-in-out
            ${styles.scale}
            ${styles.innerGlow}
          `}
        />
        
        {/* Inner circle with content */}
        <div 
          className={`
            absolute inset-8 rounded-full
            bg-zinc-900/80 backdrop-blur-md
            flex flex-col items-center justify-center
            border border-white/10
            transition-all duration-1000 ease-in-out
            ${styles.scale}
            overflow-hidden
          `}
        >
          {/* Start text - fades out when playing */}
          <div 
            className={`
              absolute inset-0 flex flex-col items-center justify-center
              transition-all duration-500 ease-in-out
              ${showStartText && !isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}
          >
            <span className="text-zinc-500 text-sm">Press play to</span>
            <span className="text-white text-lg">begin</span>
          </div>

          {/* Synced text display - fades in when playing */}
          <div 
            className={`
              absolute inset-0 flex flex-col items-center justify-center p-4
              transition-all duration-500 ease-in-out
              ${isPlaying ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}
          >
            {displayText ? (
              <p 
                key={displayText}
                className="text-white text-center text-sm md:text-base lg:text-lg font-light leading-relaxed animate-fade-in px-2"
              >
                {displayText}
              </p>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="flex space-x-1">
                  <span className="w-2 h-2 bg-violet-400/60 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                  <span className="w-2 h-2 bg-violet-400/60 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                  <span className="w-2 h-2 bg-violet-400/60 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                </div>
                <span className="text-zinc-500 text-xs mt-3">Listening...</span>
              </div>
            )}
          </div>
        </div>

        {/* Animated particles */}
        {isPlaying && (
          <>
            <div className="absolute inset-0 animate-pulse-slow">
              <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-violet-400 rounded-full" />
              <div className="absolute top-1/3 right-1/4 w-0.5 h-0.5 bg-teal-400 rounded-full" />
              <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-amber-400 rounded-full" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BreathingCircle;
